import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { APP_PATHS } from '@/app/routes/paths';
import { useNotificationsQuery } from '@/features/notifications/useNotificationsQuery';
import { BellIcon } from '@/shared/icons';
import { iconTouchTargetSquareClassName } from '@/shared/config/theme';
import { cn } from '@/shared/lib/cn';

function formatNotificationBadgeCount(count: number): string {
  if (count > 99) {
    return '99+';
  }
  return String(count);
}

export default function NotificationBell() {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();

  const { data } = useNotificationsQuery({ tab: 'you' });

  // Only surface *actionable* pending requests in the bell badge:
  //   • Follow requests           (data.pending.joinFollowRequestCount)
  //   • Class-join + admin invite (data.pending.joinBoardRequestCount)
  //
  // Unread notification-feed items (likes, comments, mentions, etc.) are
  // read-only and do NOT contribute to this count — the user asked for a
  // pure "N requests waiting for you" badge.
  const pendingCount = useMemo(() => {
    const follow = data?.pending?.joinFollowRequestCount ?? 0;
    const joinBoard = data?.pending?.joinBoardRequestCount ?? 0;
    return follow + joinBoard;
  }, [data?.pending?.joinFollowRequestCount, data?.pending?.joinBoardRequestCount]);

  const handleOpen = () => {
    navigate(APP_PATHS.notifications);
  };

  return (
    <button
      type="button"
      onClick={handleOpen}
      className={cn(iconTouchTargetSquareClassName, 'relative')}
      aria-label={
        pendingCount > 0
          ? t('notificationsPendingAria', { count: pendingCount })
          : t('notificationsAria')
      }
    >
      <BellIcon className="h-6 w-6 text-gray-700" />
      {pendingCount > 0 ? (
        <span
          className={cn(
            'absolute -right-0.5 -top-0.5 z-10 flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center',
            'rounded-full bg-brand-solid px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white tabular-nums',
          )}
        >
          {formatNotificationBadgeCount(pendingCount)}
        </span>
      ) : null}
    </button>
  );
}
